/**
 * Data layer for the Invitations Dashboard.
 *
 * Three small hooks instead of one mega-hook because they have very different
 * lifetimes: the list re-fires on every filter change, stats only invalidate
 * on lifecycle actions, and history only loads when the timeline modal opens.
 *
 * Search is debounced to 300ms inside `useInvitationsList` so the user can
 * type without slamming the API on every keystroke.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  archiveInvitation,
  cancelInvitation,
  deleteInvitation,
  extendInvitation,
  getInvitationHistory,
  getInvitationStats,
  listInvitations,
  restoreInvitation,
} from '@/src/infrastructure/lib/api';
import type {
  ApiInvitation,
  InvitationHistoryEntry,
  InvitationListQuery,
  InvitationStats,
  PaginatedResponse,
} from '@/src/types';

export function useInvitationsList(query: InvitationListQuery, refreshKey?: number) {
  const [data, setData] = useState<PaginatedResponse<ApiInvitation> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const reload = useCallback(async (q: InvitationListQuery, debounced = false) => {
    const id = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    if (debounced) {
      await new Promise((r) => setTimeout(r, 300));
      // If another request kicked off while we were debouncing, abandon this one.
      if (id !== reqIdRef.current) return;
    }
    try {
      const res = await listInvitations(q);
      // Last-write-wins guard against out-of-order responses.
      if (id !== reqIdRef.current) return;
      setData(res);
    } catch (err) {
      if (id !== reqIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      if (id === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Debounce only when search is active — pagination / tab clicks should
    // feel instant, not gated by the 300ms search debounce.
    void reload(query, Boolean(query.search));
  }, [
    reload,
    refreshKey,
    query.page,
    query.limit,
    query.search,
    query.tab,
    query.status,
    query.occasionType,
    query.sortBy,
    query.sortOrder,
    query.startDate,
    query.endDate,
  ]);

  return { data, loading, error, refresh: () => reload(query) };
}

export function useInvitationStats(refreshKey: number) {
  const [stats, setStats] = useState<InvitationStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getInvitationStats()
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load stats');
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return { stats, error };
}

export function useInvitationHistory(invitationId: string | null) {
  const [entries, setEntries] = useState<InvitationHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invitationId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInvitationHistory(invitationId)
      .then((res) => {
        if (!cancelled) setEntries(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invitationId]);

  return { entries, loading, error };
}

export const invitationActions = {
  archive: archiveInvitation,
  restore: restoreInvitation,
  cancel: cancelInvitation,
  delete: deleteInvitation,
  extend: extendInvitation,
};
